import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Container, OverlayTrigger, Row } from 'react-bootstrap'
import { EditorState } from 'lexical'
import { nanoid } from 'nanoid'

import { RichTextToolbarPlugin } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { WritableLexicalEditor } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import {
  CitationNode,
  getCitationNodesFromEditorState,
} from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNode'
import { getEditorStateFromHtml } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { LEXICAL_NODES } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { HTML_TYPE } from '../../apps/professional-app/fa/MeetingSummary/copy'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'

import { EmailResponseInteractionResult, EmailResponseResult } from '../../models/EmailResponse'
import { AIAgentMessage, AIAgentResult, AIAgentType } from '../../models/AIAgent'
import { useExportToPDF } from '../../components/PDFExportButton/hooks'
import { Button } from '../../components/Button/Button'
import { Modal } from '../../components/Modal/Modal'

import { AIAgentResultState, EditableAIAgentResult, EditButtonComponentProps, getEditorContent } from './EditableAIAgentResult/EditableAIAgentResult'
import { AIAgentResultCitations, Citation } from './AIAgentResultCitations/AIAgentResultCitations'
import { AIAgentLastUpdated } from './AIAgentLastUpdated/AIAgentLastUpdated'
import { useAIAgentResultCopy } from './hooks/useAIAgentResultCopy'
import { AIAgentLoader } from './AIAgentLoader/AIAgentLoader'
import { useEmailResponse } from './hooks/useEmailResponse'
import { AIAgentChat } from './Chat/AIAgentChat'

import './EmailResponseModal.scss'
import { CitationNodeContext } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNodeContext'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import { HeaderResultOptionsButton } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/ResultCard/Header/HeaderResultOptions/HeaderResultOptionsButton'

const MAX_EMAIL_LENGTH = 5000

export const EMAIL_RESPONSE_PDF_STYLES = `
      .email-response-container {
        height: auto !important;
      }
    `

interface EmailResponseModalProps {
  contactId: string
  show: boolean
  onCancel: () => void
}

export const EmailResponseModal: React.FC<EmailResponseModalProps> = ({ contactId, show, onCancel }) => {
  const [page, setPage] = useState<'execution' | 'interaction' | null>(null)

  const {
    isLoading,
    interactionIsLoading,
    existingEmailResponse,
    emailResponse,
    messages,
    handleEmailResponseChatInteraction,
    handleEmailResponseEditInteraction,
    handleEmailResponseExecution,
    citations,
    citationIsLoading,
  } = useEmailResponse({ contactId })

  useEffect(() => {
    if (isLoading) {
      return
    }
    setPage(emailResponse ? 'interaction' : 'execution')
  }, [isLoading, emailResponse])

  const handleRerun = () => {
    setPage('execution')
  }

  return (
    <Modal
      size={isLoading || page === 'interaction' ? 'xl' : 'lg'}
      show={show}
      onHide={onCancel}
      header={
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>E-mail reply</h4>
        </Col>
      }
      body={
        isLoading || !page ? (
          <AIAgentLoader />
        ) : page === 'execution' ? (
          <EmailResponseModalExecutionBody handleExecution={handleEmailResponseExecution} />
        ) : (
          <EmailResponseModalInteractionBody
            contactId={contactId}
            isLoading={interactionIsLoading}
            interactionIsLoading={interactionIsLoading}
            messages={messages}
            emailResponse={emailResponse}
            existingEmailResponse={existingEmailResponse}
            handleChatInteraction={handleEmailResponseChatInteraction}
            handleEditInteraction={handleEmailResponseEditInteraction}
            handleRerun={handleRerun}
            citations={citations}
            citationIsLoading={citationIsLoading}
          />
        )
      }
    />
  )
}

interface EmailResponseModalExecutionBodyProps {
  handleExecution: (email: string) => Promise<void>
}

const EmailResponseModalExecutionBody: React.FC<EmailResponseModalExecutionBodyProps> = ({ handleExecution }) => {
  const [email, setEmail] = useState<string | null>(null)

  const handleGenerate = useCallback(async () => {
    if (!email) {
      return
    }
    await handleExecution(email)
  }, [email, handleExecution])

  const handleChange = (editorState: EditorState) => {
    const editorContent = getEditorContent(editorState)
    setEmail(editorContent ? editorContent[HTML_TYPE] : null)
  }

  return (
    <Container className='p-2 d-flex flex-column gap-4'>
      <Row>
        <Col className='d-flex flex-column gap-4'>
          <span className='paragraph-medium text-center'>
            You can copy your client e-mail that you would like to answer below and Zocks will generate a reply for you based on past meetings, information
            stored in the CRM and other contact info.
          </span>
          <span className='paragraph-medium text-center'>You can fine tune the e-mail reply to your liking with our AI Agent before sending it out.</span>
        </Col>
      </Row>
      <Row>
        <Col className='p-0 d-flex flex-column gap-3'>
          <WritableLexicalEditor
            onChange={handleChange}
            className='flex-column-reverse align-items-start'
            editorContainerClassName='flex-grow-1 bg-white email-response-execution-container'
            containerClassName='w-100'
            lexicalNodes={LEXICAL_NODES}
            header={
              <RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2'>
                <div className='editable-text-result-rich-text-divider' />
              </RichTextToolbarPlugin>
            }
          />
          {!!email && email.length > MAX_EMAIL_LENGTH && (
            <span className='paragraph-small text-end zocks-text-danger'>
              E-mail exceeds maximum length ({MAX_EMAIL_LENGTH} / {email.length})
            </span>
          )}
        </Col>
      </Row>
      <Row className='align-self-end mb-4'>
        <Col className='p-0'>
          <div className='d-flex'>
            <Button variant='primary' onClick={handleGenerate} disabled={!email || email.length > MAX_EMAIL_LENGTH}>
              Generate reply
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

interface EmailResponseModalInteractionBodyProps {
  contactId: string
  messages: AIAgentMessage[]
  isLoading: boolean
  interactionIsLoading: boolean
  emailResponse: EmailResponseResult | null
  existingEmailResponse: AIAgentResult | null
  handleRerun: () => void
  handleChatInteraction: (chatMessage: string) => Promise<void>
  handleEditInteraction: (editedEmail: string) => Promise<EmailResponseInteractionResult | undefined>
  citations: Citation[] | null
  citationIsLoading: boolean
}

const EmailResponseModalInteractionBody: React.FC<EmailResponseModalInteractionBodyProps> = ({
  contactId,
  messages,
  isLoading,
  interactionIsLoading,
  emailResponse,
  existingEmailResponse,
  handleRerun,
  handleChatInteraction,
  handleEditInteraction,
  citations,
  citationIsLoading,
}) => {
  const { tracked } = useTracking()

  const exportPDFRef = useRef(null)
  const { exportToPDF } = useExportToPDF(exportPDFRef, EMAIL_RESPONSE_PDF_STYLES)

  const emailResponseState = useMemo<AIAgentResultState | null>(() => {
    if (!emailResponse?.email) {
      return null
    }
    return {
      html: emailResponse.email,
      editorState: getEditorStateFromHtml(emailResponse.email, [...LEXICAL_NODES, CitationNode]),
      displayId: nanoid(),
    }
  }, [emailResponse?.email])

  const filteredCitations = useMemo(() => {
    if (!citations || !emailResponseState) {
      return []
    }
    const citationNodes = getCitationNodesFromEditorState(emailResponseState.editorState)
    const nodeExistsForCitation = (citation: Citation) => citationNodes.find((e) => e.getCitationId() === citation.id)
    return citations.filter((citation) => nodeExistsForCitation(citation))
  }, [citations, emailResponseState])

  const { handleCopy: _handleCopy, handleMailToClicked: _handleMailToClicked } = useAIAgentResultCopy({
    aiAgentResultState: emailResponseState,
    lexicalNodes: [...LEXICAL_NODES, CitationNode],
  })

  const handleCopy = tracked(_handleCopy, {
    eventName: TrackEvent.EMAIL_RESPONSE_COPIED,
    eventDetails: { contactId },
  })

  const handleMailToClicked = tracked(_handleMailToClicked, {
    eventName: TrackEvent.EMAIL_RESPONSE_MAILTO_CLICKED,
    eventDetails: { contactId },
  })

  const handleExportToPDF = tracked(exportToPDF, {
    eventName: TrackEvent.EXPORT_TO_PDF,
    eventDetails: { contactId, type: 'emailResponse' },
  })

  const ActionButtonsComponent = () => {
    return (
      <HeaderResultOptionsButton
        resultOptions={[
          {
            selectOption: {
              id: 'send',
              label: 'Send',
            },
            icon: 'bi bi-send',
            handleSelected: handleMailToClicked,
          },
          {
            selectOption: {
              id: 'copy',
              label: 'Copy',
            },
            icon: 'bi bi-copy',
            handleSelected: handleCopy,
          },
        ]}
        disabled={false}
      />
    )
  }

  const EditButtonComponent: React.FC<EditButtonComponentProps> = ({ handleClick }) => {
    return (
      <OverlayTrigger
        trigger={citationIsLoading ? ['hover', 'focus'] : []}
        placement='top'
        overlay={<BootstrapTooltip className='zocks-text-gray-500'>Editing is disabled while references are being generated. Please hold on.</BootstrapTooltip>}
      >
        <Button className='button-large p-2' variant='primary' onClick={handleClick} disabled={isLoading || interactionIsLoading || citationIsLoading}>
          <i className='bi bi-pencil' />
          <span className='button-small'>Edit</span>
        </Button>
      </OverlayTrigger>
    )
  }

  return (
    <Container className='d-flex flex-column'>
      <Row>
        <Col xs={12} lg={8} ref={exportPDFRef}>
          <div className='p-3 zocks-bg-color-gray-300 zocks-border-1.0 email-response-container'>
            <div className='d-flex flex-column gap-3 h-100 email-response-result-container overflow-y-auto overflow-x-hidden'>
              {emailResponseState ? (
                <CitationNodeContext.Provider value={{ citations: filteredCitations }}>
                  <EditableAIAgentResult
                    aiAgentResultState={emailResponseState}
                    handleInteraction={handleEditInteraction}
                    isLoading={isLoading}
                    interactionIsLoading={interactionIsLoading}
                    ActionButtonsComponent={ActionButtonsComponent}
                    EditButtonComponent={EditButtonComponent}
                    lexicalNodes={[...LEXICAL_NODES, CitationNode]}
                  />
                </CitationNodeContext.Provider>
              ) : null}
              <AIAgentResultCitations aiAgentType={AIAgentType.EMAIL_RESPONSE} citationIsLoading={citationIsLoading} citations={filteredCitations} />
              <AIAgentLastUpdated existingAgentResult={existingEmailResponse} />
            </div>
          </div>
        </Col>
        <Col xs={12} lg={4}>
          <AIAgentChat
            messages={messages}
            handleSubmit={handleChatInteraction}
            isLoading={interactionIsLoading}
            disabled={interactionIsLoading || citationIsLoading}
          />
        </Col>
      </Row>
      <Row className='py-4'>
        <Col className='p-0 d-flex align-items-center gap-3'>
          <Button className='button-medium p-0' onClick={handleRerun}>
            <i className='bi bi-arrow-return-left' />
            Reply to a different e-mail
          </Button>
          <Button className='button-medium py-0' onClick={handleExportToPDF}>
            <i className='bi bi-file-pdf' /> Export to PDF
          </Button>
        </Col>
      </Row>
    </Container>
  )
}
